import React from 'react';
import { NavLink } from 'react-router';
import { X } from 'lucide-react';
import type { MobileManageItem } from '../leagueNav';

export interface LscMobileManageSheetProps {
  open: boolean;
  leagueName?: string;
  items: MobileManageItem[];
  onClose: () => void;
}

function sheetLinkClass(isActive: boolean): string {
  return [
    'flex items-center justify-between rounded-lg px-3 py-3 min-h-11 text-sm transition-colors',
    isActive
      ? 'bg-primary-900/50 text-primary-200 border border-primary-700'
      : 'text-secondary-100 hover:bg-secondary-800 border border-transparent',
  ].join(' ');
}

const LscMobileManageSheet: React.FC<LscMobileManageSheetProps> = ({
  open,
  leagueName,
  items,
  onClose,
}) => {
  if (!open) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-50 bg-black/60 md:hidden"
        aria-label="Close manage league menu"
        onClick={onClose}
        data-testid="lsc-mobile-manage-sheet-backdrop"
      />
      <div
        className="fixed inset-x-0 bottom-0 z-50 md:hidden rounded-t-2xl border border-secondary-700 bg-secondary-900 shadow-2xl"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Manage league"
        data-testid="lsc-mobile-manage-sheet"
      >
        <div className="flex items-center justify-between border-b border-secondary-700 px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-secondary-500">Manage league</p>
            {leagueName ? (
              <p className="truncate text-sm font-semibold text-white">{leagueName}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-lg p-2.5 min-h-11 min-w-11 text-secondary-400 hover:bg-secondary-800 hover:text-white"
            onClick={onClose}
            aria-label="Close manage league menu"
            data-testid="lsc-mobile-manage-sheet-close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <nav
          className="space-y-1 p-4"
          aria-label="Manage league"
          data-testid="lsc-mobile-manage-sheet-nav"
        >
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => sheetLinkClass(isActive)}
              onClick={onClose}
              data-testid={`lsc-mobile-manage-${item.navKey}`}
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </>
  );
};

export default LscMobileManageSheet;
